import { useCallback, useEffect, useState } from 'react';
import { apiFailureReason, emberApi, type Knock, type KnocksResponse } from '../api';
import { trackFailed } from '../../../../../shared-web/track';

/**
 * Fired on window whenever the letters may have moved (2026-09-08 走查: four
 * letters answered on the tree left the top badge saying 4 until the next
 * navigation): a letter answered anywhere on the page, a note left on a
 * read, or the conversation poll seeing the count of waiting letters move.
 * The badge and every mounted useKnocks re-read the one endpoint; nothing
 * keeps a second copy of the truth.
 */
export const KNOCKS_CHANGED_EVENT = 'ember:knocks-changed';

/** Say so: the badge and every mounted useKnocks re-read. */
export function notifyKnocksChanged(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(KNOCKS_CHANGED_EVENT));
}

/**
 * The actor's letters (v4.0 §4.9) — the share asks, the reads on either
 * side, the questions, the room invitations — read on mount and again on
 * every KNOCKS_CHANGED_EVENT; patched in place as the person answers
 * (`update`, which also fires the event). `enabled=false` skips the fetch
 * (a preview page, a session still loading). A failed load is recorded
 * (ui.failed knocks_load — the letters are the one door to the network side)
 * and otherwise silent: the card is a courtesy, never the page.
 *
 * Until 2026-09-18 the list was read once per mount: a letter that landed
 * while the person stayed on the page (the pass runs on the written Ember,
 * about a minute after it arrives) waited for their next full load. From
 * 2026-10-01 the hook kept its own clock (a re-read every minute while
 * visible); since 2026-10-21 the live channel (live.ts) fires the event the
 * moment a letter lands, and the hook re-reads on it and on each return to
 * the foreground — no clock (§4.14: the envelope is a state view, there as
 * soon as the fact is). The hidden page does not ask.
 */

export function useKnocks(enabled = true): {
  knocks: Knock[];
  /** The first read has landed: an empty list is then a fact, not a wait. */
  loaded: boolean;
  update: (next: Knock) => void;
} {
  const [data, setData] = useState<KnocksResponse | null>(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    const read = (): void => {
      emberApi.knocks()
        .then((r) => { if (alive) setData(r); })
        .catch((err: unknown) => { if (alive) trackFailed('knocks_load', 'load_failed', { error_detail: apiFailureReason(err) }); });
    };
    read();
    const tick = (): void => { if (document.visibilityState === 'visible') read(); };
    window.addEventListener(KNOCKS_CHANGED_EVENT, read);
    document.addEventListener('visibilitychange', tick);
    return () => {
      alive = false;
      window.removeEventListener(KNOCKS_CHANGED_EVENT, read);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [enabled]);
  const update = useCallback((next: Knock): void => {
    setData((cur) => {
      if (!cur) return cur;
      const patch = <T extends Knock>(rows: T[]): T[] => rows.map((k) => (
        k.match_id === next.match_id && k.role === next.role ? (next as T) : k
      ));
      return {
        seeker: cur.seeker, owner: cur.owner,
        ...(cur.shares ? { shares: patch(cur.shares) } : {}),
        ...(cur.reads ? { reads: patch(cur.reads) } : {}),
        ...(cur.questions ? { questions: { asker: patch(cur.questions.asker), answerer: patch(cur.questions.answerer) } } : {}),
        ...(cur.rooms ? { rooms: patch(cur.rooms) } : {}),
      };
    });
    notifyKnocksChanged();
  }, []);
  // The share asks and the reads (§4.9), the question rows (§4.19) and the
  // room invitations (§4.21) ride one list: the tree, the door line and the
  // paper treat them all as letters.
  return { knocks: data ? [...(data.shares ?? []), ...(data.reads ?? []), ...(data.questions?.asker ?? []), ...(data.questions?.answerer ?? []), ...(data.rooms ?? [])] : [], loaded: data !== null, update };
}
